import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { productivityAPI } from '@/services/productivity';
import { FileText, Download, Printer } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';

const ReportsPage = () => {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(false);

  // ✅ Helper: Download CSV
  const downloadCSV = (data, filename) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(obj => Object.values(obj).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // ✅ Export CSV from API
  const handleExportCSV = async () => {
    setLoading(true);
    try {
      const response = await productivityAPI.getWeeklyStats(user.id);
      if (response.success) {
        const stats = response.data;
        const flatData = stats.dailyData.map(d => ({
          Date: d.date,
          Day: d.dayName,
          FocusMinutes: d.focusMinutes,
          CompletedTasks: d.completedTasks,
          Sessions: d.sessionsCount
        }));
        
        downloadCSV(flatData, `Productivity_Report_${new Date().toISOString().split('T')[0]}.csv`);
        addNotification("Export Success", "Data exported to CSV successfully", "success");
      }
    } catch (error) {
      console.error('Export failed:', error);
      addNotification("Export Failed", "Could not export data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Helmet><title>Reports & Export - Productivity</title></Helmet>

      <div>
        <h1 className="text-3xl font-bold text-navy-steal">Reports & Exports</h1>
        <p className="text-stone-500">Download your productivity data or print summaries.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
               <FileText className="w-5 h-5 text-green-600" />
               Excel / CSV Export
            </CardTitle>
            <CardDescription>Get raw data for your own analysis in spreadsheet format.</CardDescription>
          </CardHeader>
          <CardContent>
             <p className="text-sm text-stone-500 mb-6">
               Includes daily breakdown of focus minutes, task completion counts, and session frequency for the last 7 days.
             </p>
             <Button 
               onClick={handleExportCSV} 
               disabled={loading}
               className="w-full bg-green-600 hover:bg-green-700"
             >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" /> Download CSV
                  </>
                )}
             </Button>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
               <Printer className="w-5 h-5 text-blue-600" />
               Printable Summary (PDF)
            </CardTitle>
            <CardDescription>Generate a clean, printable view of your weekly performance.</CardDescription>
          </CardHeader>
          <CardContent>
             <p className="text-sm text-stone-500 mb-6">
               Use your browser's "Save as PDF" feature in the print dialog to generate a PDF report of the current dashboard views.
             </p>
             <Button onClick={handlePrint} variant="outline" className="w-full">
                <Printer className="w-4 h-4 mr-2" /> Open Print View
             </Button>
          </CardContent>
        </Card>
      </div>
      
      {/* Hidden Print-only section for better PDF output */}
      <div className="hidden print:block mt-8">
         <h2 className="text-2xl font-bold mb-4">Weekly Productivity Report</h2>
         <p className="mb-4">Generated on: {new Date().toLocaleDateString()}</p>
         <p>User: {user.name}</p>
         {/* In a real app, we would render a full table here for print only */}
         <div className="mt-8 p-4 border rounded border-stone-800">
            <p className="text-center italic">End of Report</p>
         </div>
      </div>
    </div>
  );
};

export default ReportsPage;
